import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Boxes, Plus } from 'lucide-react';
import FormCard from './FormCard';
import EquipmentItemCard from '../equipment/EquipmentItemCard';
import EquipmentItemForm from '../equipment/EquipmentItemForm';
import { MachineFormValues } from '../types';

export default function EquipmentCard() {
    const { t } = useTranslation();
    const form = useFormContext<MachineFormValues>();
    const items = useFieldArray({ control: form.control, name: 'equipment' });
    const [editing, setEditing] = useState<number | null>(null);
    const [adding, setAdding] = useState(false);

    return (
        <FormCard
            icon={Boxes}
            title={t('roomOverview.equipment.title')}
            description={t('roomOverview.equipment.description')}
            aside={
                <Badge
                    variant="outline"
                    className="shrink-0 border-subtle text-xs font-medium text-subtle-foreground"
                >
                    {t('roomOverview.equipment.addedCount', {
                        count: items.fields.length,
                    })}
                </Badge>
            }
        >
            <div className="space-y-4">
                {items.fields.length > 0 && (
                    <div className="space-y-3">
                        {items.fields.map((item, index) =>
                            editing === index ? (
                                <EquipmentItemForm
                                    key={item.id}
                                    bare
                                    initial={item}
                                    onSave={(values) => {
                                        items.update(index, {
                                            ...values,
                                            id: item.id,
                                        });
                                        setEditing(null);
                                    }}
                                    onCancel={() => setEditing(null)}
                                />
                            ) : (
                                <EquipmentItemCard
                                    key={item.id}
                                    item={item}
                                    linksEnabled={false}
                                    onEdit={() => setEditing(index)}
                                    onRemove={() => {
                                        items.remove(index);
                                        if (editing === index) setEditing(null);
                                    }}
                                />
                            )
                        )}
                    </div>
                )}

                {adding ? (
                    <EquipmentItemForm
                        bare
                        onSave={(values) => {
                            items.append({ ...values, id: uuidv4() });
                            setAdding(false);
                        }}
                        onCancel={() => setAdding(false)}
                    />
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAdding(true)}
                    >
                        <Plus className="h-3.5 w-3.5" />
                        {t('roomOverview.equipment.addNew')}
                    </Button>
                )}
            </div>
        </FormCard>
    );
}
